// askSmart-ui/src/pages/TOSP/leaseIntake.ts
import {
  extractLease,
  LeaseExtraction,
} from "./components/LeaseReview/leaseReviewFields";
import {
  fetchStoredLease,
  LeaseServiceChoice,
  resolveStoredLease,
} from "./components/LeaseReview/leaseSource";
import { Tenant } from "./types";

// Reading a lease during Add Tenant: find it, open it, run the rules.
//
// The same three steps the Review Lease workspace takes, without the
// workspace — the wizard reads the document to find out which spaces it covers
// and what they rent for, and asks the admin about the units.

export type LeaseIntake =
  | {
      status: "ok";
      extraction: LeaseExtraction;
      /** Kept so the review screen doesn't fetch the same PDF a second time. */
      bytes: Uint8Array;
      /** The service the lease came from, to be recorded on the tenancy. */
      serviceId: string;
    }
  /**
   * The contact holds more than one tenancy, so which lease this one means is
   * a question only the admin can answer. Asked once — the answer is saved on
   * the tenant record and never asked again.
   */
  | { status: "choose"; services: LeaseServiceChoice[] }
  | { status: "missing"; reason: string };

export async function readLeaseFor(
  email: string,
  /** Set once the admin has picked, or from a tenancy that already knows. */
  serviceId?: string,
): Promise<LeaseIntake> {
  const found = await resolveStoredLease({
    primaryContactEmail: email,
    smrtDocsServiceId: serviceId || "",
  } as Tenant);

  if (found.status === "missing") return { status: "missing", reason: found.reason };
  if (found.status === "choose") return { status: "choose", services: found.services };

  try {
    const bytes = await fetchStoredLease(found.lease);
    return {
      status: "ok",
      extraction: await extractLease(bytes),
      bytes,
      serviceId: found.lease.serviceId,
    };
  } catch (err) {
    return {
      status: "missing",
      reason: err instanceof Error ? err.message : "Couldn't open the lease.",
    };
  }
}
